/**
 * Beyond Sure — JARVIS error boundary (L17 carry-forward).
 *
 * Wraps the JARVIS hub so ANY render error inside it is caught and shown as a
 * contained fallback — the app shell, every other tab, and every other
 * experimental tool keep working. This is the React side of the isolation
 * contract documented in docs/EXPERIMENTAL_FRAMEWORK.md (rule #6: error
 * containment) — a failing experiment can never take down the core product.
 *
 * Matches the IndianErrorBoundary pattern verbatim — same component shape,
 * same `role="alert"` semantics, same `data-testid` convention.
 */
import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
}
interface State {
  failed: boolean;
}

export class JarvisErrorBoundary extends Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(_error: Error, _info: ErrorInfo): void {
    // Contained on purpose — do not rethrow. A real deployment would log to its
    // own experimental-tool telemetry, never the core error stream.
  }

  render() {
    if (this.state.failed) {
      return (
        <div
          role="alert"
          data-testid="jarvis-error-boundary"
          className="m-6 rounded-2xl border border-[var(--color-paper-60)] bg-[var(--color-paper)] p-6 text-[var(--color-ink)]"
        >
          <p className="text-sm font-semibold">JARVIS hit an error.</p>
          <p className="mt-1 text-sm text-[var(--color-ink-40)]">
            Fully contained — the rest of Beyond Sure is unaffected. Reload to retry.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}
